import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "../data/site";
import { fadeUp } from "../lib/animations";

const FILTERS = [
  { id: "all", label: "All work" },
  { id: "xr", label: "Extended Reality" },
  { id: "learning", label: "Digital Learning" },
  { id: "creative", label: "Creative" },
];

const CATEGORY_GROUP = {
  "Extended Reality": "xr",
  "Augmented Reality": "xr",
  "Digital Learning": "learning",
  "Animation & VFX": "creative",
  "Video Production": "creative",
  "Audio & Media": "creative",
};

export const Portfolio = () => {
  const [active, setActive] = useState("all");

  const items = PORTFOLIO.filter(
    (item) => active === "all" || CATEGORY_GROUP[item.category] === active
  );

  return (
    <section id="work" className="bg-white py-24 sm:py-32" data-testid="portfolio-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink/40">
              Selected work
            </span>
            <h2 className="mt-4 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-none">
              Media that moves people.
            </h2>
          </div>
          <a
            href="#contact"
            data-testid="portfolio-cta"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-ink hover:text-brand transition-colors"
          >
            Have a project in mind?
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        {/* Filter bar */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mt-10 flex flex-wrap gap-2.5"
          data-testid="portfolio-filters"
        >
          {FILTERS.map((f) => {
            const isActive = active === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                data-testid={`portfolio-filter-${f.id}`}
                aria-pressed={isActive}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 border ${
                  isActive
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-white text-brand-ink/70 border-gray-200 hover:border-brand hover:text-brand"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </motion.div>

        <motion.div layout className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <AnimatePresence>
            {items.map((item) => (
              <motion.div
                key={item.slug}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`col-span-1 ${item.span}`}
              >
                <Link
                  to={`/work/${item.slug}`}
                  data-testid={`portfolio-item-${item.slug}`}
                  className="group relative block h-full overflow-hidden rounded-2xl border border-gray-200 bg-brand-surface"
                >
                  <div className="relative h-72 sm:h-80 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent opacity-70" />
                    <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-white/70">
                          {item.category}
                        </div>
                        <div className="mt-1.5 font-heading font-bold text-xl text-white">
                          {item.title}
                        </div>
                      </div>
                      <div className="h-10 w-10 shrink-0 rounded-full bg-white flex items-center justify-center translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-300">
                        <ArrowUpRight className="h-5 w-5 text-brand-ink" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
